<template>
  <RightSidebar :isActive.sync="isActive" size="large">
    <template #header>
      <img width="40" :src="ProductIcon" class="mr-4" />
      <div>
        <h4>Venue stock product</h4>
        <small v-if="product"> {{ product.stockProduct.number }} • {{ product.stockProduct.name }} </small>
      </div>
    </template>

    <template #content>
      <Loading v-if="isLoading" />
      <div v-else>
        <vs-collapse class="p-0">
          <vs-collapse-item>
            <div slot="header" class="flex items-center">
              <span>Stock product info</span>
              <small class="ml-4 text-xs">Click to expand</small>
            </div>

            <section class="ml-0 px-2 grid grid-cols-4 gap-x-8">
              <SidebarItem label="Number">
                <vs-input class="w-full" v-model="product.stockProduct.number" :disabled="true" />
              </SidebarItem>

              <SidebarItem label="Country">
                <vs-select v-model="product.stockProduct.countryId" class="w-full" :disabled="true">
                  <vs-select-item :key="index" :value="item.countryId.id" :text="item.name" v-for="(item, index) in countries" />
                </vs-select>
              </SidebarItem>

              <SidebarItem
                label="Status"
                :tooltip="
                  product.stockProduct.inactive && hasUnlockDate
                    ? `Will automatically unlock at ${formatDate(
                        product.stockProduct.lockAction.when
                      )}. You can manually unlock it at any given time prior to this`
                    : null
                "
              >
                <vs-select v-model="product.stockProduct.inactive" class="w-full" :disabled="true">
                  <vs-select-item
                    :key="item.value"
                    :value="item.value"
                    :text="item.name"
                    v-for="item in [
                      { value: false, name: 'Unlocked' },
                      { value: true, name: 'Locked' }
                    ]"
                  />
                </vs-select>
              </SidebarItem>

              <vs-divider class="col-span-full" />

              <SidebarItem label="Name" class="col-span-2">
                <vs-input class="w-full" v-model="product.stockProduct.name" :disabled="true" />
              </SidebarItem>

              <SidebarItem label="Image" class="col-span-1 row-span-2">
                <div
                  v-if="product.stockProduct.imageUrl"
                  class="w-32 h-32 rounded border border-solid flex items-center justify-center p-2"
                  style="border-color: #eee"
                >
                  <img :src="product.stockProduct.imageUrl" class="h-full" v-img:stockProduct />
                </div>
                <div v-else class="w-32 h-32 rounded border border-dashed flex items-center justify-center" style="border-color: #ccc">
                  <feather-icon icon="ImageIcon" svg-classes="h-5 w-5" class="opacity-50" />
                </div>
              </SidebarItem>

              <SidebarItem label="Color" class="col-span-1 row-span-2">
                <input
                  type="color"
                  v-model="product.stockProduct.color"
                  class="w-32 h-32 rounded-lg border border-solid custom-color-picker"
                  style="border-color: #eee"
                  :style="'--color-picker-color:' + product.stockProduct.color + '62'"
                  :disabled="true"
                />
              </SidebarItem>

              <SidebarItem label="Vendor">
                <vs-select :disabled="true" v-model="product.stockProduct.vendorId" autocomplete class="w-full">
                  <vs-select-item :key="index" :value="item.vendorId.id" :text="item.name" v-for="(item, index) in vendors" />
                </vs-select>
              </SidebarItem>

              <SidebarItem label="Vendor item number">
                <vs-input :disabled="true" class="w-full" v-model="product.stockProduct.vendorItemNumber" />
              </SidebarItem>

              <SidebarItem label="Alternative vendor">
                <vs-select :disabled="true" v-model="product.stockProduct.alternativeVendorId" autocomplete class="w-full">
                  <vs-select-item :key="index" :value="item.vendorId.id" :text="item.name" v-for="(item, index) in vendors" />
                </vs-select>
              </SidebarItem>

              <SidebarItem label="Alt. vendor item number">
                <vs-input :disabled="true" class="w-full" v-model="product.stockProduct.alternativeVendorItemNumber" />
              </SidebarItem>

              <SidebarItem label="Brand owner" class="col-span-2">
                <vs-input :disabled="true" class="w-full" v-model="product.stockProduct.brandOwner" />
              </SidebarItem>

              <SidebarItem label="Tags" class="col-span-full">
                <vs-chips placeholder="" class="w-full py-0 pointer-events-none" remove-icon="x" v-model="product.stockProduct.tags">
                  <vs-chip :key="tag" v-for="tag in product.stockProduct.tags" class="my-1 ml-2 mr-0">
                    {{ tag }}
                  </vs-chip>
                </vs-chips>
              </SidebarItem>

              <vs-divider class="col-span-full" />

              <SidebarItem label="Category">
                <vs-select :disabled="true" v-model="product.stockProduct.productCategoryId" class="w-full">
                  <vs-select-item
                    :key="index"
                    :value="item.productCategoryId.id"
                    :text="item.name"
                    v-for="(item, index) in productCategories"
                  />
                </vs-select>
              </SidebarItem>

              <!-- <SidebarItem label="Sub category">
                <vs-select
                  :disabled="true"
                  v-model="product.stockProduct.productSubCategoryId"
                  class="w-full"
                >
                  <vs-select-item key="empty" :value="null" text="None" />
                  <vs-select-item
                    :key="index"
                    :value="item.productSubCategoryId.id"
                    :text="item.name"
                    v-for="(item, index) in productSubCategories"
                  />
                </vs-select>
              </SidebarItem> -->
              <SidebarItem label="Product group">
                <vs-select :disabled="true" v-model="product.stockProduct.productGroupId" class="w-full">
                  <vs-select-item key="empty" :value="null" text="None" />
                  <vs-select-item
                    :key="index"
                    :value="item.stockProductGroupId.id"
                    :text="item.name"
                    v-for="(item, index) in validSubGroups"
                  />
                </vs-select>
              </SidebarItem>

              <SidebarItem label="Pouring category">
                <vs-select :disabled="true" v-model="product.stockProduct.pouringCategoryId" class="w-full">
                  <vs-select-item
                    :key="index"
                    :value="item.pouringCategoryId.id"
                    :text="item.name"
                    v-for="(item, index) in pouringCategories"
                  />
                </vs-select>
              </SidebarItem>

              <SidebarItem label="Stock type">
                <vs-select :disabled="true" v-model="product.stockProduct.containerType" class="w-full">
                  <vs-select-item key="empty" :value="null" text="Other" />
                  <vs-select-item :key="item" :value="item" :text="item" v-for="item in stockContainerTypes" />
                </vs-select>
              </SidebarItem>

              <vs-divider class="col-span-full" />

              <SidebarItem label="VAT rate">
                <vs-select v-model="product.stockProduct.taxGroupId" class="w-full" :disabled="true">
                  <vs-select-item :key="index" :value="item.taxGroupId.id" :text="item.code" v-for="(item, index) in taxGroups" />
                </vs-select>
              </SidebarItem>

              <SidebarItem label="Product account group">
                <vs-select v-model="product.stockProduct.productAccountGroupId" class="w-full" :disabled="true">
                  <vs-select-item
                    :key="index"
                    :value="item.productCategoryId.id"
                    :text="item.name"
                    v-for="(item, index) in productCategories"
                  />
                </vs-select>
              </SidebarItem>

              <SidebarItem label="Item account group">
                <vs-select v-model="product.stockProduct.itemAccountGroupId" class="w-full" :disabled="true">
                  <vs-select-item
                    :key="index"
                    :value="item.productCategoryId.id"
                    :text="item.name"
                    v-for="(item, index) in productCategories"
                  />
                </vs-select>
              </SidebarItem>

              <SidebarItem label="Max allowed stock deviation">
                <NumberInput v-model="product.stockProduct.allowedDeviationPercentage" unit="%" :disabled="true" />
              </SidebarItem>

              <vs-divider class="col-span-full" />

              <SidebarItem label="Case size">
                <NumberInput v-model="product.stockProduct.caseSize" unit="units" :disabled="true" />
              </SidebarItem>

              <SidebarItem label="Unit price">
                <NumberInput v-model="product.stockProduct.unitPrice" unit="DKK" :disabled="true" />
              </SidebarItem>

              <SidebarItem label="Unit amount in liters">
                <NumberInput v-model="product.stockProduct.productUnitInLiters" unit="liters" :disabled="true" />
              </SidebarItem>

              <SidebarItem label="Unit type">
                <vs-select v-model="product.stockProduct.productUnit" :disabled="true">
                  <vs-select-item :key="unit" :value="unit" :text="unit" v-for="unit in units" />
                </vs-select>
              </SidebarItem>
            </section>
          </vs-collapse-item>
        </vs-collapse>

        <vs-divider class="mt-0" />

        <section class="px-6 grid grid-cols-4 gap-x-8">
          <SidebarItem label="Is listed?">
            <vs-select v-model="product.isDelisted" :disabled="!isProcurementView">
              <vs-select-item
                :key="item.value"
                :value="item.value"
                :text="item.name"
                v-for="item in [
                  { value: false, name: 'Listed' },
                  { value: true, name: 'Delisted' }
                ]"
              />
            </vs-select>
          </SidebarItem>
          <SidebarItem label="Is Mandatory?">
            <vs-select v-model="product.isMandatory" :disabled="!isProcurementView">
              <vs-select-item
                :key="item.value"
                :value="item.value"
                :text="item.name"
                v-for="item in [
                  { value: false, name: 'Optional' },
                  { value: true, name: 'Mandatory' }
                ]"
              />
            </vs-select>
          </SidebarItem>

          <vs-divider class="col-span-full" />

          <div class="col-span-full">
            <h3 class="mb-2">Stock amounts</h3>

            <div class="grid grid-cols-3 gap-x-4">
              <vs-card v-for="storage in product.storageAmounts" :key="storage.storageIdentifier.storageId" class="px-3">
                <div class="text-center font-bold text-base mb-3">
                  {{ storage.storageIdentifier.name }}
                </div>
                <div class="grid grid-cols-2 items-center text-base">
                  <div>Stock</div>
                  <div class="text-lg text-right my-1">
                    {{ storage.amount | numberFormatDK }}
                    {{ product.stockProduct.productUnit }}
                  </div>
                  <!-- <div>Minimum</div>
                  <div class="text-right my-1">
                    <NumberInput
                      v-model="storage.minimumAmount"
                      unit="pcs"
                      :compact="true"
                    />
                  </div> -->
                  <div>Desired</div>
                  <div class="text-right my-1">
                    <NumberInput v-model="storage.desiredAmount" :unit="product.stockProduct.productUnit" :compact="true" />
                  </div>
                </div>
              </vs-card>
            </div>

            <h5 class="mb-1">Venue overall</h5>
            <div class="ml-2 mb-2 grid grid-cols-2 max-w-sm gap-y-1">
              <b>Venue amount:</b>
              <div class="text-right">
                {{ product.venueAmount | numberFormatDK }}
                {{ product.stockProduct.productUnit }}
              </div>

              <b>Venue desired:</b>
              <div class="text-right">
                {{ venueDesired | numberFormatDK }}
                {{ product.stockProduct.productUnit }}
              </div>

              <b>Venue minimum:</b>
              <NumberInput v-model="product.venueMinimumAmount" :unit="product.stockProduct.productUnit" :compact="true" />
              <!-- <div class="text-right">
                {{ venueMinimum | numberFormatDK }} pcs
              </div> -->
            </div>
          </div>
        </section>
      </div>
    </template>

    <template #footer>
      <vs-button @click="submitData" :disabled="!isFormValid">Save</vs-button>
      <vs-button type="border" color="danger" class="ml-6" @click="isActive = false">Cancel</vs-button>
    </template>
  </RightSidebar>
</template>

<script>
import ProductIcon from "@/assets/images/product-icon.png";
import moment from "moment";
import { mapGetters } from "vuex";
import VenueSelector from "@/components/VenueSelector.vue";
import RightSidebar from "@/components/Custom/RightSidebar.vue";
import SidebarItem from "@/components/Custom/SidebarItem.vue";
import NumberSelectInput from "@/components/Custom/NumberSelectInput.vue";
import NumberInput from "@/components/Custom/NumberInput.vue";
import _ from "lodash";

export default {
  components: {
    VenueSelector,
    RightSidebar,
    SidebarItem,
    NumberSelectInput,
    NumberInput
  },
  props: {
    isProcurementView: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      ProductIcon,

      isActive: false,
      isLoading: true,

      product: null,

      enableUnlockDateField: false,
      hoveringLockField: false
    };
  },
  computed: {
    ...mapGetters({
      activeVenue: "venues/activeVenue",
      activeCountry: "venues/activeCountry",
      countries: "pos/countries/countries",
      vendors: "pos/vendors/vendors",
      stockContainerTypes: "pos/countrySettings/stockContainerTypes",
      productCategories: "pos/countrySettings/stockCategories",
      pouringCategories: "pos/countrySettings/pouringCategories",
      taxGroups: "pos/countrySettings/taxGroups",
      units: "pos/countrySettings/units"
    }),
    validSubGroups() {
      if (!this.product) return [];
      var category = this.productCategories.find(x => x.productCategoryId.id === this.product.stockProduct.productCategoryId);

      if (category) return category.validProductGroups;
      else return [];
    },
    hasUnlockDate() {
      return (
        this.product.lockAction &&
        this.product.lockAction.when &&
        moment(this.product.lockAction.when).year() > 2000 && // Over year 2000 (aka over DateTime.MinValue in C#)
        moment(this.product.lockAction.when).isAfter(moment())
      );
    },
    isFormValid() {
      return true;
    },
    venueMinimum() {
      return _.sumBy(this.product.storageAmounts, e => parseFloat(e.minimumAmount));
    },
    venueDesired() {
      return _.sumBy(this.product.storageAmounts, e => parseFloat(e.desiredAmount));
    }
  },
  methods: {
    async show(assignedStockProductId) {
      this.isActive = true;
      this.isLoading = true;

      this.product = await this.$store.dispatch("pos/assignedStockProducts/getAssignedStockProduct", assignedStockProductId);
      console.log("Product:", this.product);
      await this.loadExternalData();

      this.isLoading = false;
    },

    async loadExternalData() {
      await Promise.all([
        // this.$store.dispatch("pos/countries/getCountries"),
        this.$store.dispatch("pos/countrySettings/getCountrySettings"),
        this.$store.dispatch("pos/vendors/getVendors")
      ]);
    },

    formatDate(date) {
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

    async submitData() {
      console.log("Submitting DTO", this.product);
      try {
        const updateAssignedStockProductDto = {
          isDelisted: this.product.isDelisted,
          isMandatory: this.product.isMandatory,
          venueMinimumAmount: this.product.venueMinimumAmount,
          storagesDesiredAmount: Object.fromEntries(this.product.storageAmounts.map(e => [e.storageIdentifier.storageId, e.desiredAmount]))
        };

        await this.$store.dispatch("pos/assignedStockProducts/updateAssignedStockProduct", {
          assignedStockProductId: this.product.id,
          assignedStockProductDto: updateAssignedStockProductDto
        });

        this.$store.dispatch("pos/assignedStockProducts/getAssignedStockProductsByVenue", this.product.venueId);

        this.product = null;
        this.isActive = false;
        this.isLoading = true;

        this.$vs.notify({
          title: "Success",
          text: `Successfully updated venue stock product`
        });
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          color: "danger",
          text: `Failed to update venue stock product: ${error}`
        });

        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss">
.custom-color-picker {
  appearance: none;
  border: none;
  background-color: transparent;

  &::-webkit-color-swatch {
    border: none;
    border-radius: 12px;
    box-shadow: 2px 4px 12px 0 var(--color-picker-color);
  }
  &::-webkit-color-swatch-wrapper {
    padding: 16px;
  }
}
</style>